import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

const callout = cva(
  "rounded-[var(--radius-card)] border border-l-3 border-rule px-4 py-3 font-sans text-prose",
  {
    variants: {
      // The same severity layer badges use, for the same reason: a warning that
      // borrowed the accent would stop reading as a warning the moment branding
      // changed.
      tone: {
        neutral: "border-l-rule-strong bg-surface-2 text-ink-2",
        // `info`, not `accent`. This drew the one tone that means "a standing
        // fact you cannot act on" in the brand colour, three lines under a
        // comment saying why a severity must never borrow it. The token set
        // has had an info role all along; only this component reached past it.
        info: "border-l-info bg-info-soft/40 text-ink",
        positive: "border-l-positive bg-positive-soft/50 text-ink",
        warning: "border-l-warning bg-warning-soft/60 text-ink",
        critical: "border-l-critical bg-critical-soft/60 text-ink",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface CalloutProps
  // `title` is a string on a div and a node here, so the DOM attribute is the
  // one that gives way — a tooltip on a callout is not a thing anybody wants.
  extends Omit<HTMLAttributes<HTMLDivElement>, "title">, VariantProps<typeof callout> {
  /** Optional lead line. Say what is true, not what kind of message this is. */
  title?: ReactNode;
  children?: ReactNode;
}

/**
 * Something the reader needs to know before they act.
 *
 * The rule this component exists to enforce is about the words rather than the
 * box: **say the consequence, not the mechanism.** "These are production
 * credentials — anything a build using them writes is real player data" tells
 * somebody what will happen to them. "Environment type: production" tells them
 * what we call it. The second is what a console writes when nobody decided.
 *
 * It is a component rather than a local div in one screen because the last time
 * this voice appeared it was invented on one page and never reached the other
 * eighty-six.
 */
export function Callout({ className, tone, title, children, ...props }: CalloutProps) {
  return (
    <div className={cn(callout({ tone }), className)} {...props}>
      {title ? <p className="font-medium text-ink">{title}</p> : null}
      {children ? <div className={cn(title && "mt-1", "text-ink-2")}>{children}</div> : null}
    </div>
  );
}
